import React, { createContext, useContext, useReducer, useEffect } from 'react';

import { campus } from './Campus';
import { demographics } from './Demographics';
import { defaultYear } from '../components/DefaultYear';


function getInitialState()  {
    const stateId = document.querySelector('#survey-app')?.getAttribute('data-state');
    const firstDemographicKey = demographics[0].id;

    // Query string keys: demo, question, year, search
    const queryString = new URLSearchParams(window.location.search);
    const demo = queryString.get('demo');
    const question = queryString.get('question');
    const year = queryString.get('year');
    const search = queryString.get('search');

    return {
        isChartLoading: true,
        isInitialLoading: true,

        campusId: campus,
        stateId,

        currentQuestionId: question || null,
        currentQuestion: null,
        selectedDemographic: demo || firstDemographicKey,
        selectedYear: year || defaultYear,
        searchQuery: search || '',

        questions: [],
        topics: [],
        chartData: null,
        error: null
    };
}

const initialState = getInitialState();

const ACTIONS = {
    SET_CHART_LOADING: 'SET_CHART_LOADING',
    SET_INITIAL_LOADING: 'SET_INITIAL_LOADING',
    SET_DEMOGRAPHIC: 'SET_DEMOGRAPHIC',
    SET_YEAR: 'SET_YEAR',
    SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
    SET_TOPICS: 'SET_TOPICS',
    SET_QUESTIONS: 'SET_QUESTIONS',
    SET_CURRENT_QUESTION: 'SET_CURRENT_QUESTION',
    SET_CURRENT_QUESTION_ID: 'SET_CURRENT_QUESTION_ID',
    SET_CHART_DATA: 'SET_CHART_DATA',
    SET_ERROR: 'SET_ERROR',
};

const Context = createContext();

const surveyReducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_CHART_LOADING:
            return {
                ...state,
                isChartLoading: action.payload,
            };
        case ACTIONS.SET_INITIAL_LOADING:
            return {
                ...state,
                isInitialLoading: action.payload,
            };
        case ACTIONS.SET_DEMOGRAPHIC:
            return {
                ...state,
                selectedDemographic: action.payload,
            };
        case ACTIONS.SET_YEAR:
            return {
                ...state,
                selectedYear: action.payload,
            };
        case ACTIONS.SET_SEARCH_QUERY:
            return {
                ...state,
                searchQuery: action.payload,
            };
        case ACTIONS.SET_TOPICS:
            return {
                ...state,
                topics: action.payload,
                error: null,
            };
        case ACTIONS.SET_QUESTIONS:
            return {
                ...state,
                questions: action.payload,
                error: null,
            };
        case ACTIONS.SET_CHART_DATA:
            return {
                ...state,
                chartData: action.payload,
                isChartLoading: false,
                error: null,
            };
        case ACTIONS.SET_ERROR:
            return {
                ...state,
                error: action.payload,
                isChartLoading: false,
                isInitialLoading: false,
            };
        case ACTIONS.SET_CURRENT_QUESTION:
            return {
                ...state,
                currentQuestion: action.payload,
            };
        case ACTIONS.SET_CURRENT_QUESTION_ID:
            return {
                ...state,
                currentQuestionId: action.payload,
            };
        default:
            return state;
    }
};

export const SurveyProvider = ({ children }) => {
    const [state, dispatch] = useReducer(surveyReducer, initialState);

    const fetchQuestions = async () => {
        const { searchQuery, selectedYear } = state;

        let url = `/api/survey/questions?year=${selectedYear}`;

        if (searchQuery) {
            url += `&search=${encodeURIComponent(searchQuery)}`;
        }

        try {
            const response = await fetch(url);
            const data = await response.json();

            if(data.error) {
                throw new Error(data.message);
            }

            const topics = data.topics;
            const questions = topics.flatMap(topic => topic.questions || []);

            dispatch({ type: ACTIONS.SET_TOPICS, payload: topics });
            dispatch({ type: ACTIONS.SET_QUESTIONS, payload: questions });

            // Handle current question selection
            if (questions.length > 0) {
                if (state.currentQuestionId) {
                    // Find the question object by ID from URL
                    const questionObject = questions.find(q => q.id == state.currentQuestionId);
                    if (questionObject) {
                        dispatch({ type: ACTIONS.SET_CURRENT_QUESTION, payload: questionObject });
                    } else {
                        // Question not found, default to first question
                        console.warn(`Question with ID ${state.currentQuestionId} not found in loaded questions, defaulting to first question`);
                        dispatch({ type: ACTIONS.SET_CURRENT_QUESTION, payload: questions[0] });
                    }
                } else {
                    // No question ID from URL, default to first question
                    dispatch({ type: ACTIONS.SET_CURRENT_QUESTION, payload: questions[0] });
                }
            }

            if (state.isInitialLoading) {
                dispatch({ type: ACTIONS.SET_INITIAL_LOADING, payload: false });
            }

        } catch (error) {
            if (state.isInitialLoading) {
                dispatch({ type: ACTIONS.SET_INITIAL_LOADING, payload: false });
            }
            dispatch({ type: ACTIONS.SET_ERROR, payload: error.message || 'An error occurred while fetching data' });
        }
    }

    const fetchChartData = async () => {
        const { currentQuestion, selectedYear, selectedDemographic } = state;
        const questionId = currentQuestion.id;

        if (!questionId) return;

        dispatch({ type: ACTIONS.SET_CHART_LOADING, payload: true });

        let url = `/api/survey/question/${questionId}/responses?year=${selectedYear}&demographic=${selectedDemographic}`;

        if (state.campusId) {
            url += `&school_id=${state.campusId}`;
        }

        if (state.stateId) {
            url += `&state=${state.stateId}`;
        }

        try {
            const response = await fetch(url);
            const data = await response.json();

            if(data.error) {
                throw new Error(data.message);
            }

            dispatch({ type: ACTIONS.SET_CHART_DATA, payload: data });

        } catch (error) {
            dispatch({ type: ACTIONS.SET_ERROR, payload: error.message || 'An error occurred while fetching data' });
        }
    }

    const setDemographic = (demographic) => {
        dispatch({ type: ACTIONS.SET_CHART_LOADING, payload: true });
        dispatch({ type: ACTIONS.SET_DEMOGRAPHIC, payload: demographic });
    };

    const setYear = (year) => {
        dispatch({ type: ACTIONS.SET_CHART_LOADING, payload: true });
        dispatch({ type: ACTIONS.SET_YEAR, payload: year });
    };

    const setCurrentQuestion = (question) => {
        dispatch({ type: ACTIONS.SET_CHART_LOADING, payload: true });
        dispatch({ type: ACTIONS.SET_CURRENT_QUESTION, payload: question });
        dispatch({ type: ACTIONS.SET_CURRENT_QUESTION_ID, payload: question.id });
    };

    const setSearchQuery = (query) => {
        dispatch({ type: ACTIONS.SET_SEARCH_QUERY, payload: query });
    };

    const getNextQuestion = () => {
        dispatch({ type: ACTIONS.SET_CHART_LOADING, payload: true });

        const { questions, currentQuestion } = state;

        if (!currentQuestion || questions.length === 0) return null;

        const currentIndex = questions.findIndex(q => q.id === currentQuestion.id);

        if (currentIndex === -1) return null;

        // Go to next question, or first if at the end
        const newIndex = currentIndex === questions.length - 1 ? 0 : currentIndex + 1;
        return questions[newIndex];
    };

    const getPreviousQuestion = () => {
        dispatch({ type: ACTIONS.SET_CHART_LOADING, payload: true });

        const { questions, currentQuestion } = state;

        if (!currentQuestion || questions.length === 0) return null;

        const currentIndex = questions.findIndex(q => q.id === currentQuestion.id);

        if (currentIndex === -1) return null;

        // Go to previous question, or last if at the beginning
        const newIndex = currentIndex === 0 ? questions.length - 1 : currentIndex - 1;
        return questions[newIndex];
    };

    const goToNextQuestion = () => {
        const nextQuestion = getNextQuestion();
        if (nextQuestion) {
            setCurrentQuestion(nextQuestion);
        }
    };

    const goToPreviousQuestion = () => {
        const previousQuestion = getPreviousQuestion();
        if (previousQuestion) {
            setCurrentQuestion(previousQuestion);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchQuestions();
    }, []);

    // Fetch questions when the year changes
    useEffect(() => {
        fetchQuestions();
    }, [state.selectedYear, state.searchQuery]);

    // Fetch chart data when the question or year changes or the demographic changes
    useEffect(() => {
        if (state.currentQuestion && state.selectedYear) {
            fetchChartData();
        }
    }, [state.currentQuestion, state.selectedYear, state.selectedDemographic]);

    // Update URL query string when state values change
    useEffect(() => {
        const url = new URL(window.location);
        const searchParams = url.searchParams;

        // Update query parameters based on state values
        if (state.currentQuestionId) {
            searchParams.set('question', state.currentQuestionId);
        } else {
            searchParams.delete('question');
        }

        if (state.selectedDemographic) {
            searchParams.set('demo', state.selectedDemographic);
        } else {
            searchParams.delete('demo');
        }

        if (state.selectedYear) {
            searchParams.set('year', state.selectedYear);
        } else {
            searchParams.delete('year');
        }

        if (state.searchQuery) {
            searchParams.set('search', state.searchQuery);
        } else {
            searchParams.delete('search');
        }

        // Update URL without adding to history
        window.history.replaceState(null, '', url.toString());
    }, [state.currentQuestionId, state.selectedDemographic, state.selectedYear, state.searchQuery]);

    const actions = {
        setDemographic,
        setYear,
        setCurrentQuestion,
        setSearchQuery,
        getNextQuestion,
        getPreviousQuestion,
        goToNextQuestion,
        goToPreviousQuestion,
    };

    const value = {
        ...state,
        ...actions,
    };

    return (
        <Context.Provider value={value}>
            {children}
        </Context.Provider>
    );
};

export const useSurvey = () => {
    const context = useContext(Context);
    if (!context) {
        throw new Error('useSurvey must be used within a SurveyProvider');
    }
    return context;
};
